import { BitkitFilter } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';

import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { ChartCursorContextProvider } from '@/pages/Insights/Context/ChartCursorContext';

import ResourceChart from './components/ResourceUsageMonitoring/ResourceChart';
import { useResourceUsageData } from './hooks/useResourceUsageData';

type ResourceUsageMonitoringProps = {
  isEnabled: boolean;
};

const ResourceUsageMonitoring = ({ isEnabled }: ResourceUsageMonitoringProps) => {
  const build = useBuildDetailsPageStore((state) => state.build);
  const summary = useBuildDetailsPageStore((state) => state.logSummary);
  const traceToken = useBuildDetailsPageStore((state) => state.traceToken ?? '');

  const {
    step,
    setStep,
    steps,
    select,
    setSelect,
    chartLoading,
    setChartLoading,
    chartDatas,
    identifiersData,
    isIdentifiersLoading,
    apiDataError,
    isApiDataError,
    refetch,
    globalMinTimestamp,
    globalMaxTimestamp,
  } = useResourceUsageData(build, isEnabled, traceToken, summary);

  return (
    <>
      <BitkitFilter marginBlockEnd="16" marginBlockStart="-16" state={isIdentifiersLoading ? 'loading' : undefined}>
        <BitkitFilter.Select
          emptyLabel="All Steps"
          items={steps.map(({ title }) => ({ label: title, value: title }))}
          label="Step"
          loading={steps.length === 0}
          loadingLabel="Waiting for build to finish…"
          onValueChange={(newStep) => {
            setChartLoading({
              cpu: true,
              memory: true,
              loadAverage: true,
              swap: true,
              networkInterface: true,
              mountPath: true,
              disk: true,
            });
            setStep(newStep);
          }}
          pluralLabel="Steps"
          preserveOrder
          value={step}
        />
      </BitkitFilter>
      <Box
        display="grid"
        gridTemplateColumns={['minmax(0, 1fr)', 'minmax(0, 1fr) minmax(0, 1fr)']}
        gridGap="32"
        maxWidth="100%"
      >
        <ChartCursorContextProvider>
          {chartDatas.map(({ title, key, data, dataUnit, type, threshold, tooltipText, placeholder }) => {
            const optionsMap = {
              networkInterface: identifiersData?.network_interfaces || [],
              mountPath: identifiersData?.mount_paths || [],
              disk: identifiersData?.disks || [],
            };
            const options = optionsMap[key as keyof typeof optionsMap];

            const handleSelectChange =
              key in optionsMap
                ? (value: string) => {
                    setSelect({
                      ...select,
                      [key]: value ? [value] : [],
                    });
                    setChartLoading((prev) => ({ ...prev, [key]: true }));
                  }
                : undefined;

            return (
              <ResourceChart
                key={key}
                title={title}
                data={data}
                dataUnit={dataUnit}
                type={type}
                threshold={threshold}
                tooltipText={tooltipText}
                placeholder={placeholder}
                options={options}
                isLoading={chartLoading[key]}
                error={apiDataError}
                isError={isApiDataError}
                selectValue={select[key as keyof typeof select]?.[0]}
                onSelectChange={handleSelectChange}
                refetch={refetch}
                chartStart={globalMinTimestamp}
                chartEnd={globalMaxTimestamp}
              />
            );
          })}
        </ChartCursorContextProvider>
      </Box>
    </>
  );
};

export default ResourceUsageMonitoring;
